import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Play, 
  Wifi,
  Phone,
  AlertTriangle,
  Volume2,
  DoorOpen,
  Bell,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { locationActionService } from '../../services';
import LocationActionModal from './LocationActionModal';
import type { LocationAction, LocationActionType, LocationActionTriggerResponse, LocationActionTestResponse } from '../../types';

interface LocationActionsTabProps {
  locationId: number;
}

// Icon and color mapping for action types
const ACTION_TYPE_CONFIG: Record<LocationActionType, { icon: React.ReactNode; color: string; label: string }> = {
  GATE_CONTROLLER: { icon: <DoorOpen size={16} />, color: 'text-blue-500 bg-blue-500/10', label: 'Gate' },
  SIREN: { icon: <Volume2 size={16} />, color: 'text-orange-500 bg-orange-500/10', label: 'Siren' },
  CALL_911: { icon: <AlertTriangle size={16} />, color: 'text-red-500 bg-red-500/10', label: '911' },
  CALL_SECURITY: { icon: <Phone size={16} />, color: 'text-green-500 bg-green-500/10', label: 'Security' },
  CALL_RESIDENT: { icon: <Phone size={16} />, color: 'text-indigo-500 bg-indigo-500/10', label: 'Resident' },
  INTERCOM: { icon: <Bell size={16} />, color: 'text-purple-500 bg-purple-500/10', label: 'Intercom' },
};

const LocationActionsTab: React.FC<LocationActionsTabProps> = ({ locationId }) => {
  const [actions, setActions] = useState<LocationAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Modal states
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<LocationAction | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [actionToDelete, setActionToDelete] = useState<LocationAction | null>(null);
  
  // Test/Trigger states
  const [testingActionId, setTestingActionId] = useState<number | null>(null);
  const [triggeringActionId, setTriggeringActionId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ actionId: number; result: LocationActionTestResponse } | null>(null);
  const [triggerResult, setTriggerResult] = useState<{ actionId: number; result: LocationActionTriggerResponse } | null>(null);
  
  // Dial modal for call actions
  const [dialInfo, setDialInfo] = useState<{ phoneNumber: string; callerId: string; sipUri?: string } | null>(null);

  const fetchActions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await locationActionService.getLocationActions(locationId, { include_inactive: true });
      setActions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch actions');
    } finally {
      setIsLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // Clear success/test/trigger messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (testResult) {
      const timer = setTimeout(() => setTestResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [testResult]);

  useEffect(() => {
    if (triggerResult) {
      const timer = setTimeout(() => setTriggerResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [triggerResult]);

  const handleCreate = () => {
    setSelectedAction(null);
    setIsCreateEditModalOpen(true);
  };

  const handleEdit = (action: LocationAction) => {
    setSelectedAction(action);
    setIsCreateEditModalOpen(true);
  };

  const handleDeleteClick = (action: LocationAction) => {
    setActionToDelete(action);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!actionToDelete) return;
    
    try {
      await locationActionService.deleteAction(actionToDelete.id);
      setSuccessMessage(`Action "${actionToDelete.name}" deleted successfully`);
      setIsDeleteModalOpen(false);
      setActionToDelete(null);
      fetchActions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete action');
    }
  };

  const handleTest = async (action: LocationAction) => {
    try {
      setTestingActionId(action.id);
      setTestResult(null);
      const result = await locationActionService.testAction(action.id);
      setTestResult({ actionId: action.id, result });
    } catch (err) {
      setTestResult({ 
        actionId: action.id, 
        result: { 
          success: false, 
          reachable: false, 
          message: err instanceof Error ? err.message : 'Test failed' 
        } 
      });
    } finally {
      setTestingActionId(null);
    }
  };

  const handleTrigger = async (action: LocationAction) => {
    try {
      setTriggeringActionId(action.id);
      setTriggerResult(null);
      const result = await locationActionService.triggerAction(action.id);
      
      // For call types, show dial modal
      if (result.dial_info) {
        setDialInfo({
          phoneNumber: result.dial_info.phone_number,
          callerId: result.dial_info.caller_id,
          sipUri: result.dial_info.sip_uri,
        });
      } else {
        setTriggerResult({ actionId: action.id, result });
        if (result.success) {
          setSuccessMessage(result.message);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Trigger failed');
    } finally {
      setTriggeringActionId(null);
    }
  };

  const handleToggleStatus = async (action: LocationAction) => {
    try {
      await locationActionService.toggleActionStatus(action.id, !action.is_active);
      setSuccessMessage(`Action "${action.name}" ${action.is_active ? 'deactivated' : 'activated'}`);
      fetchActions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleDialSip = () => {
    if (dialInfo?.sipUri) {
      window.open(dialInfo.sipUri, '_blank');
    } else if (dialInfo?.phoneNumber) {
      window.open(`sip:${dialInfo.phoneNumber}`, '_blank');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard');
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (action: LocationAction) => (
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ACTION_TYPE_CONFIG[action.action_type]?.color || 'bg-gray-500/10'}`}>
            {ACTION_TYPE_CONFIG[action.action_type]?.icon}
          </div>
          <div>
            <div className="font-medium text-text-primary">{action.name}</div>
            {action.description && (
              <div className="text-xs text-text-muted truncate max-w-[200px]">{action.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'action_type',
      label: 'Type',
      render: (action: LocationAction) => (
        <Badge variant="default">
          {ACTION_TYPE_CONFIG[action.action_type]?.label || action.action_type}
        </Badge>
      ),
    },
    {
      key: 'api_url',
      label: 'API URL',
      render: (action: LocationAction) => (
        <span className="font-mono text-sm text-text-secondary truncate max-w-[200px]" title={action.api_url}>
          {action.api_url || '—'}
        </span>
      ),
    },
    {
      key: 'phone_number',
      label: 'Phone',
      render: (action: LocationAction) => (
        <span className="text-text-secondary">
          {action.phone_number || '—'}
        </span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (action: LocationAction) => (
        <button
          onClick={() => handleToggleStatus(action)}
          className="cursor-pointer"
        >
          <Badge variant={action.is_active ? 'success' : 'danger'}>
            {action.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </button>
      ),
    },
    {
      key: 'test_result',
      label: 'Test',
      render: (action: LocationAction) => {
        // Show test result for this action
        if (testResult?.actionId === action.id) {
          return testResult.result.reachable ? (
            <span className="flex items-center gap-1 text-green-500">
              <CheckCircle size={14} />
              <span className="text-xs">{testResult.result.response_time_ms}ms</span>
            </span>
          ) : (
            <span className="flex items-center gap-1 text-red-500">
              <XCircle size={14} />
              <span className="text-xs">Unreachable</span>
            </span>
          );
        }
        return null;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (action: LocationAction) => (
        <div className="flex items-center gap-1">
          {/* Test Button - Only for device types */}
          {['GATE_CONTROLLER', 'SIREN', 'INTERCOM'].includes(action.action_type) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTest(action)}
              disabled={testingActionId === action.id}
              title="Test Connectivity"
            >
              {testingActionId === action.id ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Wifi size={16} />
              )}
            </Button>
          )}
          
          {/* Trigger Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleTrigger(action)}
            disabled={triggeringActionId === action.id || !action.is_active}
            className={`${
              action.action_type === 'CALL_911' ? 'text-red-500 hover:bg-red-500/10' :
              action.action_type === 'GATE_CONTROLLER' ? 'text-blue-500 hover:bg-blue-500/10' :
              action.action_type === 'CALL_SECURITY' ? 'text-green-500 hover:bg-green-500/10' :
              'text-accent-primary'
            }`}
            title={`Trigger ${ACTION_TYPE_CONFIG[action.action_type]?.label}`}
          >
            {triggeringActionId === action.id ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
          </Button>

          {/* Edit Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(action)}
            title="Edit"
          >
            <Edit2 size={16} />
          </Button>

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteClick(action)}
            className="text-red-500 hover:bg-red-500/10"
            title="Delete"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-12">
          <Loading size="lg" text="Loading actions..." />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">Device Controls & Actions</h3>
        <Button onClick={handleCreate}>
          <Plus size={18} className="mr-2" />
          Add Action
        </Button>
      </div>

      {/* Alerts */}
      {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
      {successMessage && <Alert variant="success" message={successMessage} onClose={() => setSuccessMessage(null)} />}

      {/* Actions Table */}
      <Card>
        {actions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
              <DoorOpen size={32} className="text-text-muted" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">No actions configured</h3>
            <p className="text-text-muted mb-4">
              Add device controls like gates, sirens, or emergency contacts
            </p>
            <Button onClick={handleCreate}>
              <Plus size={18} className="mr-2" />
              Add First Action
            </Button>
          </div>
        ) : (
          <Table columns={columns} data={actions} />
        )}
      </Card>

      {/* Quick Actions Panel */}
      {actions.filter(a => a.is_active).length > 0 && (
        <Card padding="lg">
          <h4 className="text-sm font-medium text-text-secondary mb-3">Quick Actions</h4>
          <div className="flex flex-wrap gap-2">
            {actions
              .filter(a => a.is_active)
              .map(action => (
                <Button
                  key={action.id}
                  variant="secondary"
                  size="sm"
                  onClick={() => handleTrigger(action)}
                  disabled={triggeringActionId === action.id}
                  className={`${
                    action.action_type === 'CALL_911' ? 'border-red-500/50 hover:bg-red-500/10' :
                    action.action_type === 'GATE_CONTROLLER' ? 'border-blue-500/50 hover:bg-blue-500/10' :
                    action.action_type === 'CALL_SECURITY' ? 'border-green-500/50 hover:bg-green-500/10' :
                    action.action_type === 'SIREN' ? 'border-orange-500/50 hover:bg-orange-500/10' :
                    ''
                  }`}
                >
                  <span className="mr-2">{ACTION_TYPE_CONFIG[action.action_type]?.icon}</span>
                  {action.name}
                </Button>
              ))}
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <LocationActionModal
        isOpen={isCreateEditModalOpen}
        onClose={() => {
          setIsCreateEditModalOpen(false);
          setSelectedAction(null);
        }}
        onSuccess={() => {
          setSuccessMessage(selectedAction ? 'Action updated successfully' : 'Action created successfully');
          fetchActions();
        }}
        locationId={locationId}
        action={selectedAction}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setActionToDelete(null);
        }}
        title="Delete Action"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Are you sure you want to delete <strong>{actionToDelete?.name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Dial Modal for Call Actions */}
      <Modal
        isOpen={!!dialInfo}
        onClose={() => setDialInfo(null)}
        title="Dial Information"
      >
        {dialInfo && (
          <div className="space-y-4">
            <div className="bg-surface-secondary rounded-lg p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-text-primary mb-2">
                  {dialInfo.phoneNumber}
                </div>
                <div className="text-sm text-text-muted">
                  Caller ID: {dialInfo.callerId}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button onClick={handleDialSip} className="w-full">
                <Phone size={18} className="mr-2" />
                Open in Softphone
              </Button>
              <div className="flex gap-2">
                <Button 
                  variant="secondary" 
                  onClick={() => copyToClipboard(dialInfo.phoneNumber)}
                  className="flex-1"
                >
                  Copy Number
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => copyToClipboard(dialInfo.callerId)}
                  className="flex-1"
                >
                  Copy Caller ID
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setDialInfo(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LocationActionsTab;
